import { Component, TemplateFunction } from '@ribajs/core'
import { hasChildNodesTrim } from '@ribajs/utils'
import { SbPopupComponent } from './sb-popup.component.js'

import type {
  ShopifyProductVariant,
  ShopifyProduct,
  ShopifyProductVariantOption,
  ShopifyMediaImage,
  SbProductVariantImageDetails,
  SbProductVariantImageDetailsGrouped,
} from '../types/index.js'

/**
 * The types for the scope
 */
interface Scope {
  // OPTIONS / DATA

  /** The shopify product data */
  product?: ShopifyProduct
  /** The current selected variant with data */
  variant?: ShopifyProductVariant
  /** The product options, see {@link SbProductComponent.prepareOptions} */
  options: ShopifyProductVariantOption[]

  /** Extracted images by variant */
  images: SbProductVariantImageDetailsGrouped

  activeImage?: SbProductVariantImageDetails

  popupTargetId: string

  // METHODS
  onClickThumb: SbProductVariantsGalleryComponent['onClickThumb']
  showPopup: SbProductVariantsGalleryComponent['showPopup']
  hidePopup: SbProductVariantsGalleryComponent['hidePopup']
  togglePopup: SbProductVariantsGalleryComponent['togglePopup']
  onThumbLoaded: SbProductVariantsGalleryComponent['onThumbLoaded']
  onImageLoaded: SbProductVariantsGalleryComponent['onImageLoaded']

  // TEMPLATES
}

/**
 * A component to show the product (variant) images on PDP
 */
export class SbProductVariantsGalleryComponent extends Component {
  public static tagName = 'sb-product-variants-gallery'

  protected popup?: SbPopupComponent

  public scope: Scope = {
    // OPTIONS / DATA
    product: undefined,
    variant: undefined,
    options: [],
    images: {},
    activeImage: undefined,
    popupTargetId: 'sb-popup-product-variants-gallery',

    // METHODS
    onClickThumb: this.onClickThumb.bind(this),
    showPopup: this.showPopup.bind(this),
    hidePopup: this.hidePopup.bind(this),
    togglePopup: this.togglePopup.bind(this),
    onThumbLoaded: this.onThumbLoaded.bind(this),
    onImageLoaded: this.onImageLoaded.bind(this),

    // TEMPLATES
  }

  static get observedAttributes(): string[] {
    return ['product', 'variant', 'options']
  }

  protected requiredAttributes(): string[] {
    return ['product', 'variant']
  }

  protected parsedAttributeChangedCallback(
    attributeName: string,
    oldValue: any,
    newValue: any,
    namespace: string | null,
  ) {
    super.parsedAttributeChangedCallback(
      attributeName,
      oldValue,
      newValue,
      namespace,
    )
    switch (attributeName) {
      case 'variant':
        this.onVariantChanged(oldValue, newValue)
        break
      case 'product':
        // console.debug('Product changed', oldValue, newValue)
        break
    }
  }

  protected initPopup() {
    this.popup =
      document.querySelector(`sb-popup[id='${this.scope.popupTargetId}']`) ||
      undefined
    if (!this.popup) {
      throw new Error(
        `sb-popup element with id "${this.scope.popupTargetId}" not found!`,
      )
    }
  }

  public onClickThumb(image: SbProductVariantImageDetails) {
    this.setImageActive(image)
  }

  protected setAllImagesInactive() {
    for (const variantId in this.scope.images) {
      if (!this.scope.images[variantId]) {
        continue
      }

      for (const image of this.scope.images[variantId]) {
        image.active = false
      }
    }
  }

  protected setImageActive(image?: SbProductVariantImageDetails) {
    if (!image) {
      console.warn('No image selected')
      return
    }
    if (image.active) {
      return
    }
    this.setAllImagesInactive()
    image.active = true
    this.scope.activeImage = image
  }

  /**
   * Generate regex for `variant` options with or without position
   * @param variant
   * @returns
   */
  protected getVariantOptionRegex(variant: ShopifyProductVariant | null) {
    const options =
      variant?.options.map(value => value.replaceAll(' ', '')) || []

    // TODO: We currently only support one option per variant, support for multiple could look like this:
    // const regex = options.join('-');

    // Support for one option
    const regex = options[0]

    return {
      regex,
      pattern: new RegExp(`-(${regex})`, 'i'),
      patternWithIndex: new RegExp(`(${regex})-(\\d+)`, 'i'),
      variantId: variant!.id,
      variant,
    }
  }

  /**
   * Generate regex for all variants options with or without position
   * @returns
   */
  protected getVariantsOptionRegex() {
    if (!this.scope.product) {
      throw new Error('Product is required')
    }

    const result: ReturnType<
      SbProductVariantsGalleryComponent['getVariantOptionRegex']
    >[] = []

    for (const variant of this.scope.product?.variants) {
      result.push(this.getVariantOptionRegex(variant))
    }

    // Rule for any variant option regex
    result.push({
      regex: '0',
      pattern: new RegExp(`-(0)`, 'i'),
      patternWithIndex: new RegExp(`-(0)-(\\d+)`, 'i'),
      variantId: 0,
      variant: null,
    })

    // Order result by pattern length, longer patterns should be searched for first, otherwise the results may be incorrect
    return result.sort((a, b) => b.regex.length - a.regex.length)
  }

  /**
   * Get the variant which has the image linked by shopify (the normal behavior, not that using the filename)
   * @param image
   */
  protected getShopifyVariantByImage(image: ShopifyMediaImage) {
    if (!this.scope.product?.variants) return null

    for (const variant of this.scope.product?.variants) {
      if (variant.featured_media.id === image.id) return variant
    }

    return null
  }

  /**
   * Extract `variantId` and `position` from image's filename.
   * For the rule definition, see [Dokumentation Shopify Pflege](https://docs.google.com/document/d/1Rd8zu_WIXheJ6CQFzfo7-X_nieCXgJ-iLNSW8zzF9KQ/edit#heading=h.lpivyr990bne)
   * @param image
   * @param variantsRegex
   * @returns
   */
  protected getVariantForImage(
    image: ShopifyMediaImage,
    variantsRegex: ReturnType<
      SbProductVariantsGalleryComponent['getVariantsOptionRegex']
    >,
  ) {
    // Find matching image filename for variant
    for (const regex of variantsRegex) {
      if (image.src.match(regex.pattern)) {
        const [, , position] =
          image.src.match(regex.patternWithIndex) ||
          image.src.match(regex.pattern) ||
          []
        return {
          ...regex,
          position: position ? parseInt(position) || 1 : 1,
        }
      }
    }

    // As a fallback also use the image linked to the variant by shopify (not using the filename)
    const fallbackVariant = this.getShopifyVariantByImage(image)
    if (fallbackVariant) {
      return {
        variantId: fallbackVariant.id,
        variant: fallbackVariant,
        position: 1,
      }
    }

    return {
      variantId: 0,
      variant: null,
      position: 1,
    }
  }

  /**
   * Extract product (variant) image details by it's filename.
   * For the rule definition, see [Dokumentation Shopify Pflege](https://docs.google.com/document/d/1Rd8zu_WIXheJ6CQFzfo7-X_nieCXgJ-iLNSW8zzF9KQ/edit#heading=h.lpivyr990bne)
   */
  protected extractImageDetails(
    image: ShopifyMediaImage,
    variantsRegex: ReturnType<
      SbProductVariantsGalleryComponent['getVariantsOptionRegex']
    >,
  ) {
    const src = image.src

    let bgfull = false
    let borderless = false
    let border = false

    if (src.includes('borderless') && src.includes('seeberger')) {
      borderless = true
    } else if (
      (src.includes('border') && src.includes('seeberger')) ||
      (src.includes('freisteller') && src.includes('seeberger'))
    ) {
      border = true
    }
    if (src.includes('bgfull')) {
      bgfull = true
    }

    const moreDetails = this.getVariantForImage(image, variantsRegex)

    // Set fallback alt text
    if (!image.alt && this.scope.product && moreDetails.variant) {
      // title + variant title + position
      image.alt = `${this.scope.product.title} - ${moreDetails.variant.title} - ${moreDetails.position || 1}`
    }

    const result: SbProductVariantImageDetails = {
      bgfull,
      borderless,
      border,
      media: image,
      loadedFull: false,
      loadedPreview: false,
      loadedThumbnail: false,
      ...moreDetails,
    }
    return result
  }

  /**
   * Extract product (variant) image details for all images and group them by variant
   */
  protected extractImagesDetails() {
    if (!this.scope.product) {
      throw new Error('Product is required')
    }

    const variantsRegex = this.getVariantsOptionRegex()

    const variantImagesArray: SbProductVariantImageDetails[] = []

    for (const image of this.scope.product.media) {
      variantImagesArray.push(this.extractImageDetails(image, variantsRegex))
    }

    /** Image details grouped by variant */
    const grouped: SbProductVariantImageDetailsGrouped = {
      /** Image details for all variants */
      0: [],
    }

    // Init grouped by variant ids
    for (const variant of this.scope.product.variants) {
      grouped[variant.id] = []
    }

    // Group images by variant id
    for (const variantImage of variantImagesArray) {
      const id = variantImage.variantId
      grouped[id] = grouped[id] || []
      grouped[id].push(variantImage)
    }

    // Push universal images (images for all variants) to all variants
    for (const universalImage of grouped[0]) {
      for (const _id in grouped) {
        const id = Number(_id)
        if (!id || id === 0) continue
        grouped[id].push(universalImage)
      }
    }

    // Sort group by position
    for (const id in grouped) {
      if (grouped[id]) grouped[id].sort((a, b) => a.position - b.position)
    }

    // Check if a variant has no images and use the variant images of the first variant as a FALLBACK
    for (const _variantId in grouped) {
      const variantId = Number(_variantId)
      if (variantId === 0) continue
      if (!grouped[variantId].length) {
        console.warn(
          `The variant with id "${variantId}" has no images, using the images of the first variant as a fallback`,
        )

        const fallbackVariantId = this.getFirstVariantWithImages(grouped)
        if (fallbackVariantId) {
          grouped[variantId] = grouped[fallbackVariantId]
        }
      }
    }

    return grouped
  }

  protected getFirstVariantWithImages(
    grouped: SbProductVariantImageDetailsGrouped,
  ) {
    for (const _variantId in grouped) {
      const variantId = Number(_variantId)
      if (variantId === 0) continue
      if (grouped[variantId].length) return variantId
    }
  }

  /**
   * Called as soon as this custom element es detected in the dom
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbProductVariantsGalleryComponent.observedAttributes)
  }

  protected onVariantChanged(
    oldVariant: ShopifyProductVariant | undefined,
    newVariant: ShopifyProductVariant | undefined,
  ) {
    // Do not set active image if the variant has not changed
    if (oldVariant && newVariant && oldVariant.id === newVariant.id) {
      return
    }

    if (this.scope.variant !== newVariant) {
      console.error('Variant changed but something went wrong', {
        currentVariant: this.scope.variant,
        oldVariant: oldVariant,
        newVariant: newVariant,
      })
    }

    const activeVariantId = this.scope.variant?.id || 0
    this.setImageActive(this.scope.images[activeVariantId]?.[0])
  }

  protected async beforeBind(): Promise<any> {
    this.scope.images = this.extractImagesDetails()
    this.onVariantChanged(undefined, this.scope.variant)
  }

  /**
   * Called after this custom element is bound and ready
   */
  protected async afterBind() {
    await super.afterBind()
    this.initPopup()
    this.classList.add(`${SbProductVariantsGalleryComponent.tagName}-ready`)
  }

  /**
   * Returns the template, if null is returned, the child html is not replaced, otherwise the innerHTML is replaced
   */
  protected template(): ReturnType<TemplateFunction> {
    // Use this template if this custom element has no child html
    if (!hasChildNodesTrim(this)) {
      return (
        <p class="error">
          This component has no it's own template, you need to add your own
          child HTML
        </p>
      )
    }
    return null
  }

  public onThumbLoaded(
    image: SbProductVariantImageDetails,
    eventName: 'changed' | 'loaded',
  ) {
    image.loadedThumbnail = eventName === 'loaded'
  }

  public onImageLoaded(
    image: SbProductVariantImageDetails,
    eventName: 'changed' | 'loaded',
  ) {
    image.loadedPreview = eventName === 'loaded'
  }

  public hidePopup(event?: Event) {
    if (!this.popup) {
      this.error(`Popup with id "${this.scope.popupTargetId}" not found!`)
      return
    }
    event?.preventDefault()
    event?.stopPropagation()
    this.popup.hide(event)
  }

  public showPopup(event?: Event) {
    if (!this.popup) {
      this.error(`Popup with id "${this.scope.popupTargetId}" not found!`)
      return
    }
    event?.preventDefault()
    event?.stopPropagation()
    this.popup.show(event, this.scope.images[this.scope.variant?.id || 0], true)
  }

  public togglePopup(event?: Event) {
    if (!this.popup) {
      this.error(`Popup with id "${this.scope.popupTargetId}" not found!`)
      return
    }
    event?.preventDefault()
    event?.stopPropagation()
    this.popup.toggle(
      event,
      this.scope.images[this.scope.variant?.id || 0],
      true,
    )
  }
}
